import { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { ApiError } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from '@sw/auth';
import { api } from '../../lib/api-client';
import { MicrosoftLogo } from './icons';

/**
 * Botón de Microsoft Sign-In usando MSAL Browser. El id_token devuelto por Azure
 * AD se envía al backend <c>POST /api/client/auth/microsoft</c>.
 *
 * SW-310. Si <c>VITE_MICROSOFT_CLIENT_ID</c> no está configurado, el botón se
 * muestra deshabilitado (la maqueta queda visible).
 *
 * Lazily inicializa MSAL la primera vez que se clickea — evita el overhead del
 * init para usuarios que no usan Microsoft.
 */
export function MicrosoftSignInButton({
  accessLinkToken,
  disabled,
  onBlocked,
  onAuthenticated,
}: {
  accessLinkToken: string | null;
  disabled?: boolean;
  onBlocked: (email: string, domain: string) => void;
  onAuthenticated: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const msalRef = useRef<PublicClientApplication | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID as string | undefined;
  const tenantId =
    (import.meta.env.VITE_MICROSOFT_TENANT_ID as string | undefined) ?? 'common';

  // No clientId → maqueta disabled.
  if (!clientId) {
    return (
      <Button
        variant="outlined"
        size="large"
        disabled
        startIcon={<MicrosoftLogo />}
        data-testid="login-microsoft-disabled"
        sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'center', gap: 1 }}
      >
        {t('login.continueWithMicrosoft')}
      </Button>
    );
  }

  useEffect(() => {
    if (msalRef.current) return;
    const cfg: Configuration = {
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin + '/login',
      },
      cache: { cacheLocation: 'sessionStorage' },
    };
    const pca = new PublicClientApplication(cfg);
    pca
      .initialize()
      .then(() => {
        msalRef.current = pca;
        setInitialized(true);
      })
      .catch(() => setError(t('login.errors.unexpected')));
  }, [clientId, tenantId, t]);

  async function handleClick() {
    if (!msalRef.current || !accessLinkToken) return;
    setBusy(true);
    setError(null);
    try {
      const result = await msalRef.current.loginPopup({
        scopes: ['openid', 'email', 'profile'],
      });
      if (!result.idToken) {
        setError(t('login.errors.unexpected'));
        return;
      }
      await api.post('/api/client/auth/microsoft', {
        idToken: result.idToken,
        accessLinkToken,
      });
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      onAuthenticated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const body = err.body as { email?: string; domain?: string } | null;
        if (body?.email && body?.domain) {
          onBlocked(body.email, body.domain);
          return;
        }
      }
      // MSAL puede tirar errores user-cancel — los swallowamos sin mostrar nada.
      const msalError = err as { errorCode?: string };
      if (msalError?.errorCode === 'user_cancelled') return;
      setError(t('login.errors.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      <Button
        variant="outlined"
        size="large"
        disabled={disabled || busy || !initialized}
        onClick={handleClick}
        startIcon={
          busy ? <CircularProgress size={16} /> : <MicrosoftLogo />
        }
        data-testid="login-microsoft-button"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          width: '100%',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        {t('login.continueWithMicrosoft')}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="login-microsoft-error">
          {error}
        </Alert>
      )}
    </Box>
  );
}
